/**
 * Calcule la distance en mètres entre deux points géographiques (Haversine formula).
 * @param lat1 Latitude du point 1
 * @param lon1 Longitude du point 1
 * @param lat2 Latitude du point 2
 * @param lon2 Longitude du point 2
 * @returns Distance en mètres
 */
export function getDistanceFromLatLonInM(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Rayon de la terre en mètres
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance en mètres
    return d;
}

function deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
}

/**
 * Interpole un point à une certaine distance sur un segment.
 * @param p1 Point de départ [lat, lon]
 * @param p2 Point d'arrivée [lat, lon]
 * @param fraction Fraction de la distance (0 à 1)
 * @returns Point interpolé [lat, lon]
 */
export function interpolate(p1: [number, number], p2: [number, number], fraction: number): [number, number] {
    return [
        p1[0] + (p2[0] - p1[0]) * fraction,
        p1[1] + (p2[1] - p1[1]) * fraction
    ];
}

/**
 * Extrait les points du tracé entre deux PK donnés.
 * @param rawPath Le tracé complet [Lat, Lon]
 * @param startPK Le PK de début (ex: 110.5)
 * @param endPK Le PK de fin (ex: 108.2)
 * @param trackStartPK Le PK au début du tracé (ex: 119.9)
 * @param trackEndPK Le PK à la fin du tracé (ex: 98.1)
 * @returns Un tableau de points [Lat, Lon] formant le segment
 */
export function getPointsBetweenPK(
    rawPath: [number, number][],
    startPK: number,
    endPK: number,
    trackStartPK: number = 119.9,
    trackEndPK: number = 46.7
): [number, number][] {
    // Normaliser l'ordre (Max -> Min car les PK décroissent)
    const maxPK = Math.max(startPK, endPK);
    const minPK = Math.min(startPK, endPK);

    const tMaxPK = Math.max(trackStartPK, trackEndPK);
    const tMinPK = Math.min(trackStartPK, trackEndPK);

    // Si hors limites
    if (minPK > tMaxPK || maxPK < tMinPK) return [];

    const totalTrackDist = Math.abs(trackStartPK - trackEndPK) * 1000;

    // Calcul de la distance totale géo
    let totalGeoDist = 0;
    const dists = [0];
    for (let i = 0; i < rawPath.length - 1; i++) {
        const d = getDistanceFromLatLonInM(rawPath[i][0], rawPath[i][1], rawPath[i + 1][0], rawPath[i + 1][1]);
        totalGeoDist += d;
        dists.push(totalGeoDist);
    }

    const scale = totalGeoDist / totalTrackDist;

    // Distances cibles depuis le début (trackStartPK)
    const distA = Math.abs(trackStartPK - maxPK) * 1000 * scale;
    const distB = Math.abs(trackStartPK - minPK) * 1000 * scale;

    const segmentPoints: [number, number][] = [];

    // Ajouter le point exact de départ
    // Trouver l'index juste avant distA
    let idxA = 0;
    while (idxA < dists.length && dists[idxA + 1] < distA) idxA++;

    if (idxA < rawPath.length - 1) {
        const dCurrent = dists[idxA];
        const dNext = dists[idxA + 1];
        const frac = (distA - dCurrent) / (dNext - dCurrent);
        segmentPoints.push(interpolate(rawPath[idxA], rawPath[idxA + 1], frac));
    }

    // Ajouter les points intermédiaires
    for (let i = idxA + 1; i < dists.length; i++) {
        if (dists[i] > distB) break;
        segmentPoints.push(rawPath[i]);
    }

    // Ajouter le point exact de fin
    let idxB = 0;
    while (idxB < dists.length && dists[idxB + 1] < distB) idxB++;

    if (idxB < rawPath.length - 1) {
        const dCurrent = dists[idxB];
        const dNext = dists[idxB + 1];
        const frac = (distB - dCurrent) / (dNext - dCurrent);
        segmentPoints.push(interpolate(rawPath[idxB], rawPath[idxB + 1], frac));
    }

    return segmentPoints;
}

export function getPointAtPK(
    rawPath: [number, number][],
    targetPK: number,
    trackStartPK: number = 119.9,
    trackEndPK: number = 46.7
): [number, number] | null {
    const maxPK = Math.max(trackStartPK, trackEndPK);
    const minPK = Math.min(trackStartPK, trackEndPK);
    
    if (targetPK > maxPK || targetPK < minPK) return null;

    const totalTrackDist = Math.abs(trackStartPK - trackEndPK) * 1000;

    // Calcul de la distance totale géo
    let totalGeoDist = 0;
    const dists = [0];
    for (let i = 0; i < rawPath.length - 1; i++) {
        const d = getDistanceFromLatLonInM(rawPath[i][0], rawPath[i][1], rawPath[i + 1][0], rawPath[i + 1][1]);
        totalGeoDist += d;
        dists.push(totalGeoDist);
    }

    const scale = totalGeoDist / totalTrackDist;

    // Distance cible depuis le début (trackStartPK)
    const distTarget = Math.abs(trackStartPK - targetPK) * 1000 * scale;

    // Trouver l'index
    let idx = 0;
    while (idx < dists.length && dists[idx + 1] < distTarget) idx++;

    if (idx < rawPath.length - 1) {
        const dCurrent = dists[idx];
        const dNext = dists[idx + 1];
        const frac = (distTarget - dCurrent) / (dNext - dCurrent);
        return interpolate(rawPath[idx], rawPath[idx + 1], frac);
    }

    return rawPath[rawPath.length - 1];
}

/**
 * Trouve le point le plus proche sur un tracé (polyline) par rapport à un point donné.
 * @param pt Point [lat, lon]
 * @param path Tableau de points [[lat, lon], ...]
 * @returns Le point projeté, son index dans le path, et la distance.
 */
export function findNearestPointOnTrack(
    pt: [number, number],
    path: [number, number][]
): { point: [number, number], index: number, distance: number } {
    if (!path || path.length === 0) {
        return { point: pt, index: 0, distance: Infinity };
    }
    let minDistance = Infinity;
    let nearestPoint: [number, number] = path[0];
    let nearestIndex = 0;

    for (let i = 0; i < path.length - 1; i++) {
        const p1 = path[i];
        const p2 = path[i + 1];

        // Calculer la projection du point sur le segment p1-p2
        const projected = projectPointOnSegment(pt, p1, p2);
        const dist = getDistanceFromLatLonInM(pt[0], pt[1], projected[0], projected[1]);

        if (dist < minDistance) {
            minDistance = dist;
            nearestPoint = projected;
            nearestIndex = i;
        }
    }

    return { point: nearestPoint, index: nearestIndex, distance: minDistance };
}

/**
 * Projetter un point sur un segment [p1, p2].
 */
function projectPointOnSegment(p: [number, number], p1: [number, number], p2: [number, number]): [number, number] {
    const lat = p[0], lon = p[1];
    const lat1 = p1[0], lon1 = p1[1];
    const lat2 = p2[0], lon2 = p2[1];

    const dx = lat2 - lat1;
    const dy = lon2 - lon1;

    if (dx === 0 && dy === 0) return p1;

    const t = ((lat - lat1) * dx + (lon - lon1) * dy) / (dx * dx + dy * dy);

    if (t < 0) return p1;
    if (t > 1) return p2;

    return [lat1 + t * dx, lon1 + t * dy];
}
